/**
 * Conversation-turns archetype renderer (fix #252 / #PL-WEB-RUBRICS).
 *
 * Three response options after a scenario prompt. The kid taps
 * an option; the renderer flags self-correction when the kid
 * taps a different option within 4s of the first one
 * (per AC #2 — `self_correct_window_ms` defaults to 4000).
 *
 * Scoring: delegated to `scoreConversationTurns`, which maps
 * the tapped option's tag (`correct` / `partial` / `wrong`)
 * to the scoring result. Partial-credit (0.5) is unique to
 * this archetype.
 */
import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import {
  DEFAULT_SELF_CORRECT_WINDOW_MS,
  type ConversationTurnsRubric,
} from '../../../lib/practice/rubrics';
import { scoreConversationTurns } from '../../../lib/practice/scoring';
import type { ArchetypePlayerProps, TrialOutcome } from './shared';
import { PhotoIcon } from '../../../components/icons/PhotoIcon';
import { SpeakerIcon } from '../../../components/icons/SpeakerIcon';

interface Props extends ArchetypePlayerProps<ConversationTurnsRubric> {}

export default function ConversationTurnsPlayer({
  rubric,
  trialIndex,
  totalTrials,
  onTrialComplete,
}: Props): ReactElement {
  const windowMs =
    rubric.self_correct_window_ms ?? DEFAULT_SELF_CORRECT_WINDOW_MS;
  const [selected, setSelected] = useState<number | null>(null);
  const [startedAt, setStartedAt] = useState<number>(() => Date.now());
  const lastTapAtRef = useRef<number | null>(null);
  const selfCorrectedRef = useRef(false);
  const scenarioAudioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    setStartedAt(Date.now());
    setSelected(null);
    lastTapAtRef.current = null;
    selfCorrectedRef.current = false;
  }, [trialIndex]);

  function playScenario(): void {
    if (typeof window === 'undefined') return;
    // Stop any in-flight clip before starting a new one so the
    // kid never hears two scenario audios layered (fix #299).
    if (scenarioAudioRef.current) {
      scenarioAudioRef.current.pause();
      scenarioAudioRef.current.src = '';
    }
    const audio = new Audio(rubric.scenario_audio);
    scenarioAudioRef.current = audio;
    audio.play().catch(() => {
      // Network or autoplay policy — fail silently.
    });
  }

  useEffect(() => {
    playScenario();
    return () => {
      // Pause and release the Audio element on unmount / trial
      // advance so a previous trial's clip cannot leak over the
      // next one (fix #299).
      if (scenarioAudioRef.current) {
        scenarioAudioRef.current.pause();
        scenarioAudioRef.current.src = '';
        scenarioAudioRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trialIndex]);

  function onTap(idx: number): void {
    const now = Date.now();
    if (selected === null) {
      lastTapAtRef.current = now;
      selfCorrectedRef.current = false;
    } else if (
      idx !== selected &&
      lastTapAtRef.current !== null &&
      now - lastTapAtRef.current < windowMs
    ) {
      selfCorrectedRef.current = true;
      lastTapAtRef.current = now;
    } else {
      lastTapAtRef.current = now;
    }
    setSelected(idx);
  }

  function onSubmit(): void {
    if (selected === null) return;
    const score = scoreConversationTurns(selected, rubric);
    const correctIndex = rubric.options.findIndex((o) => o.tag === 'correct');
    const outcome: TrialOutcome = {
      isCorrect: score.is_correct,
      latencyMs: Date.now() - startedAt,
      selfCorrected: selfCorrectedRef.current,
      usedCue: false,
      response: selected,
      expected: correctIndex,
      promptKind: 'mixed',
    };
    onTrialComplete(outcome);
  }

  // Compute the outcome-as-of-the-current-tap so the selected
  // option button can show the kid a correct/incorrect cue the
  // instant they tap (fix #421 — applying the same immediate-
  // reinforcement pattern as PictureNaming #403 to the sibling
  // archetype renderers). The visual cue persists from first tap
  // until the kid advances; re-tapping within `windowMs`
  // re-runs the scoring against the new option and the cue
  // updates accordingly. We deliberately surface right/wrong
  // here rather than hiding the outcome — conversation_turns
  // is targeted at kids old enough to process immediate
  // correctness feedback, which is the entire pedagogical point
  // of a response-option trial.
  const correctIndex = rubric.options.findIndex((o) => o.tag === 'correct');
  const isCorrect: boolean | null =
    selected === null
      ? null
      : scoreConversationTurns(selected, rubric).is_correct;
  const selectedText =
    selected === null ? null : (rubric.options[selected]?.text ?? '');
  const correctText =
    correctIndex >= 0 ? (rubric.options[correctIndex]?.text ?? '') : '';

  return (
    <main className="screen handoff">
      <div className="auth-hero">
        <header className="screen-header">
          <h1>Trial {trialIndex + 1} / {totalTrials}</h1>
          <p className="tagline">What would you say?</p>
        </header>
      </div>
      {rubric.scenario_image_url ? (
        <img
          src={rubric.scenario_image_url}
          alt="Scenario"
          className="prompt-image"
        />
      ) : (
        <div className="prompt-placeholder" aria-hidden="true">
          <PhotoIcon size={32} aria-label="Scenario placeholder" />
        </div>
      )}
      <div className="action-row">
        <button
          type="button"
          className="link-button"
          onClick={playScenario}
        >
          <SpeakerIcon size={16} aria-hidden="true" />
          <span>Play again</span>
        </button>
      </div>
      <div
        className="choice-stack"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
          maxWidth: '480px',
          margin: '0 auto',
        }}
        role="radiogroup"
        aria-label="Response options"
      >
        {rubric.options.map((opt, idx) => {
          const isSelected = selected === idx;
          const outcomeClass =
            isSelected && isCorrect === true
              ? ' selected correct'
              : isSelected && isCorrect === false
                ? ' selected incorrect'
                : selected !== null && !isSelected
                  ? ' muted'
                  : '';
          return (
            <button
              type="button"
              key={`opt-${idx}`}
              className={`snap-button${outcomeClass}`}
              onClick={(): void => onTap(idx)}
              role="radio"
              aria-checked={isSelected}
              aria-label={
                isSelected && isCorrect === true
                  ? `Correct — ${opt.text}`
                  : isSelected && isCorrect === false
                    ? `Wrong — ${opt.text}; correct was ${correctText}`
                    : `Option ${idx + 1}: ${opt.text}`
              }
            >
              {opt.text}
            </button>
          );
        })}
      </div>
      {selected !== null ? (
        <button
          type="button"
          className="snap-button"
          onClick={onSubmit}
          aria-label={
            isCorrect === true
              ? `Got it! — correct, ${selectedText}`
              : isCorrect === false
                ? `Got it! — wrong, ${selectedText}; correct was ${correctText}`
                : 'Got it!'
          }
        >
          Got it!
        </button>
      ) : null}
    </main>
  );
}
